import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import toast from "react-hot-toast";

const StepThree = forwardRef(({ formData, setFormData, courseId }, ref) => {
  const [errors, setErrors] = useState({});
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (courseId && courseId > 0) {
      const savedDocuments = localStorage.getItem("uploadedDocuments");
      if (savedDocuments) {
        try {
          const parsedDocuments = JSON.parse(savedDocuments);
          setDocuments(parsedDocuments);
        } catch (err) {
          console.error("Error parsing documents from localStorage:", err);
        }
      } else if (formData?.documents && formData?.documents !== null) {
        try {
          const docArr =
            typeof formData.documents === "string"
              ? JSON.parse(formData.documents)
              : formData.documents;
          setDocuments(docArr);
          localStorage.setItem("uploadedDocuments", JSON.stringify(docArr));
        } catch (err) {
          console.error("Error parsing documents from formData:", err);
          setDocuments([]);
        }
      }
    } else {
      setDocuments([]);
      localStorage.removeItem("uploadedDocuments");
    }
  }, [courseId]);

  const onDropDocuments = (acceptedFiles) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/plain",
      "text/markdown",
      "text/csv",
      "application/rtf",
      "application/vnd.oasis.opendocument.text",
      "application/vnd.oasis.opendocument.presentation",
      "application/vnd.oasis.opendocument.spreadsheet",
    ];

    const newDocuments = acceptedFiles
      .filter((file) => {
        if (file.size > maxSize) {
          toast.error(`${file.name} exceeds 10MB`);
          return false;
        }
        if (!allowedTypes.includes(file.type)) {
          toast.error(`${file.name} is not a supported file type`);
          return false;
        }
        return true;
      })
      .map((file) => {
        const id = uuidv4();
        return {
          id,
          name: file.name,
          type: file.type,
          size: file.size,
          file,
          isUploaded: false,
          previewUrl: URL.createObjectURL(file),
        };
      })
      .filter(Boolean);

    if (newDocuments.length < acceptedFiles.length) {
      toast.error("Some selected files are not valid or supported");
    }

    setDocuments((prev) => {
      const updatedDocuments = [...prev, ...newDocuments];
      localStorage.setItem(
        "uploadedDocuments",
        JSON.stringify(updatedDocuments)
      );
      return updatedDocuments;
    });

    setFormData((prev) => ({
      ...prev,
      documents: [...(prev.documents || []), ...newDocuments],
    }));
  };

  const removeDocument = (id) => {
    if (documents.length > 0) {
      const updatedDocuments = documents.filter((item, index) => index !== id);
      setDocuments(updatedDocuments);
      localStorage.setItem(
        "uploadedDocuments",
        JSON.stringify(updatedDocuments)
      );
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropDocuments,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/vnd.ms-powerpoint": [".ppt"],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [".pptx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "text/plain": [".txt"],
      "text/markdown": [".md", ".markdown"],
      "text/csv": [".csv"],
      "application/rtf": [".rtf"],
      "application/vnd.oasis.opendocument.text": [".odt"],
      "application/vnd.oasis.opendocument.presentation": [".odp"],
      "application/vnd.oasis.opendocument.spreadsheet": [".ods"],
    },
    multiple: true,
  });

  useEffect(() => {
    if (documents.length > 0) {
      localStorage.setItem("uploadedDocuments", JSON.stringify(documents));
      setFormData((prev) => ({
        ...prev,
        documents,
      }));
    } else {
      localStorage.removeItem("uploadedDocuments");
      setFormData((prev) => ({
        ...prev,
        documents: [],
      }));
    }
  }, [documents]);

  const prepareDocumentsForBackend = () => {
    return documents;
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      const newErrors = {};

      if (!documents || documents.length === 0) {
        newErrors.documents = "At least one document is required";
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    getDocuments: prepareDocumentsForBackend,
  }));

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    if (typeof bytes !== "number" || isNaN(bytes)) return "Unknown size";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (!fileType) {
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 2V8H20"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    if (fileType === "application/pdf") {
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 2V8H20"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 13H8"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 17H8"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10 9H8"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    } else if (fileType.includes("word") || fileType.includes("document")) {
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 2V8H20"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 13H8"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 17H8"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10 9H8"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    } else if (
      fileType.includes("powerpoint") ||
      fileType.includes("presentation")
    ) {
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 2V8H20"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 13H8"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 17H8"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10 9H8"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    } else if (fileType.includes("excel") || fileType.includes("spreadsheet")) {
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 2V8H20"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 13H8"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 17H8"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10 9H8"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    } else {
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 2V8H20"
            stroke="#F05203"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
  };

  return (
    <div className="card-default-style step-form-holder">
      <div className="step-form-holder-inner mx-auto my-10 my-lg-17">
        <div className="mb-10 mb-sm-20 mb-md-32">
          <h2 className="ah-heading-04 fw-bold text-center mb-10 mb-sm-20 mb-md-32">
            Upload Course Document
          </h2>
          <p className="ah-text-sm fw-semibold mb-0 text-center">
            Sources let EGWB base its responses on the information that matters
            most to you. (Examples: marketing plans, course reading, research
            notes, meeting transcripts, sales documents, etc.)
          </p>
        </div>

        <div className="upload-section mb-10">
          <div
            {...getRootProps()}
            className={`upload-box d-flex justify-content-center align-content-center text-center ${
              isDragActive ? "drag-active" : ""
            }`}
            style={{
              border: "2px dashed rgba(118, 156, 25, 0.32)",
              borderRadius: "8px",
              padding: "40px 20px",
              minHeight: "200px",
              cursor: "pointer",
            }}
          >
            <input {...getInputProps()} />
            <div className="upload-content d-flex flex-column fw-bold text-black-100 gap-2">
              <div className="upload-icon mb-3 d-flex justify-content-center align-items-center mx-auto rounded-circle">
                <svg
                  width="14"
                  height="15"
                  viewBox="0 0 14 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.39087 11.0639V4.16431L4.18977 6.36541L3.00457 5.13787L7.23744 0.904998L11.4703 5.13787L10.2851 6.36541L8.08402 4.16431V11.0639H6.39087ZM2.15799 14.4502C1.69238 14.4502 1.29378 14.2844 0.962206 13.9528C0.630631 13.6213 0.464844 13.2227 0.464844 12.757V10.2173H2.15799V12.757H12.3169V10.2173H14.01V12.757C14.01 13.2227 13.8443 13.6213 13.5127 13.9528C13.1811 14.2844 12.7825 14.4502 12.3169 14.4502H2.15799Z"
                    fill="#F05203"
                  />
                </svg>
              </div>
              <div className="ah-text-md fw-semibold">
                {isDragActive ? "Drop the files here ..." : "Upload sources"}
              </div>
              <div className="ah-text-md fw-semibold">
                Drag & drop or{" "}
                <span className="theme-color fw-bold">choose file</span> to
                upload
              </div>
              <button
                type="button"
                className="btn btn-soft-green mt-3 no-shadow py-13"
                style={{ fontSize: "14px" }}
              >
                Upload a source
              </button>
            </div>
          </div>

          <div className="mt-3">
            <p className="ah-text-sm gray-color-300 mb-0 text-center">
              Supported file types: PDF, Word (.doc, .docx), PowerPoint (.ppt,
              .pptx), Excel (.xls, .xlsx), Text (.txt), Markdown (.md), CSV
              (.csv), RTF (.rtf), OpenDocument (.odt, .odp, .ods)
            </p>
            {errors.documents && (
              <div className="text-danger text-center mt-2">
                {errors.documents}
              </div>
            )}
          </div>

          {documents?.length > 0 && (
            <div className="preview mt-16 pt-16 d-flex flex-wrap border-top gap-3">
              {documents?.map((doc, index) => (
                <div className="preview-item" key={index}>
                  <div
                    className="d-flex align-items-center gap-2 py-2 px-5 border rounded"
                    style={{ minWidth: "200px" }}
                  >
                    <div className="file-icon">{getFileIcon(doc.type)}</div>
                    <div className="file-info flex-grow-1">
                      <div
                        className="file-name fw-semibold"
                        style={{ fontSize: "12px" }}
                      >
                        {doc.name && doc.name.length > 20
                          ? `${doc.name.substring(0, 20)}...`
                          : doc.name || `Document ${index + 1}`}
                      </div>
                    </div>
                    <span
                      className="btn-outline-danger theme-color ah-text-xl fw-bold cursor-pointer"
                      onClick={() => removeDocument(index)}
                    >
                      ×
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
});

export default StepThree;
